import { Card, LoadingGate, Screen } from './components/Chrome';
import { usePlatformGate } from './hooks/usePlatformGate';
import { useSession } from './hooks/useSession';
import { AdminNoticeScreen } from './screens/AdminNoticeScreen';
import { CheckInScreen } from './screens/CheckInScreen';
import { LoginScreen } from './screens/LoginScreen';
import { NotSupportedScreen } from './screens/NotSupportedScreen';
import { StaffDashboard } from './screens/StaffDashboard';

export function App() {
  const { state: gate, retry } = usePlatformGate();

  switch (gate) {
    case 'checking':
      return (
        <Screen>
          <Card>
            <LoadingGate />
          </Card>
        </Screen>
      );
    case 'blocked':
      return <NotSupportedScreen />;
    case 'unavailable':
      return <NotSupportedScreen unreachable onRetry={retry} />;
    case 'allowed':
      // Session handling lives in its own component so it only mounts once the
      // device is actually allowed in — a blocked visitor should never probe
      // /api/me, and hooks cannot be called conditionally.
      return <AuthenticatedApp />;
  }
}

function AuthenticatedApp() {
  const { session, signIn, signOut } = useSession();

  switch (session.state) {
    case 'loading':
      return (
        <Screen>
          <Card>
            <LoadingGate />
          </Card>
        </Screen>
      );

    case 'loggedOut':
      return <LoginScreen error={session.error} onSignIn={signIn} />;

    case 'loggedIn':
      // Three roles, three screens. Admins deliberately get a notice rather than
      // the dashboard: administration is Android-only (see AdminNoticeScreen).
      if (session.user.role === 'student') {
        // Keyed on identity: one phone is genuinely passed between students at
        // "get help", and without this the next signed-in account would inherit
        // the previous student's check-in state — including a settled "you're
        // marked present" they never earned. Remounting on email change throws
        // that state away. The native app does the same (see AppRoot.kt).
        return (
          <CheckInScreen key={session.user.email} email={session.user.email} onSignOut={signOut} />
        );
      }
      if (session.user.role === 'lecturer') {
        // Keyed for the same reason: a staff browser is shared too, and one
        // lecturer's courses, sessions and owner-search results must not survive
        // into the next lecturer's session.
        return <StaffDashboard key={session.user.email} onSignOut={signOut} />;
      }
      return <AdminNoticeScreen email={session.user.email} onSignOut={signOut} />;
  }
}
